/**
 * 极练 Rehab & Fit —— 训练会话状态管理（内存 + IndexedDB 持久化）（阶段 8）。
 *
 * 铁律：
 * 1. 每组即时落库，刷新可恢复未完成训练；
 * 2. 休息计时跨刷新绝对时间恢复；
 * 3. 动态从会话与用户设置获取 userId，禁止硬编码 user-demo；
 * 4. 采集 formBreakdown 与 restShort（actualRestSeconds），支持替换动作完整撤销。
 */
import { create } from "zustand";
import type {
  IncrementType,
  RirValue,
  WeightUnitScope,
  WorkoutExercise,
  WorkoutExerciseStatus,
  WorkoutSet,
  WorkoutPlanType,
} from "@jilian/shared";
import type { GeneratedExercise, ReadinessResult } from "@jilian/core";
import { uuid } from "../lib/ids";
import { EXERCISE_BY_ID } from "../data/exercises";
import {
  createWorkout,
  loadActiveWorkout,
  saveSet,
  saveExerciseItem,
  finishSession,
} from "../db/workoutRepo";
import { db } from "../db/db";

export interface SessionSet {
  id: string;
  setIndex: number;
  weightKg: number | null;
  reps: number | null;
  rir: RirValue | null;
  reachedFailure: boolean;
  muscleDiscomfort: boolean;
  jointPain: boolean;
  injuryPain: boolean;
  formBreakdown: boolean;
  restStartedAt?: string | null;
  actualRestSeconds?: number | null;
  completed: boolean;
  completedAt: string | null;
  version: number;
  updatedAt: string;
}

export interface SessionExercise {
  /** 持久化动作条目 id（workoutExercise.id）。 */
  id: string;
  exerciseId: string;
  suggestedWeightKg: number | null;
  weightUnitScope: WeightUnitScope;
  targetSets: number;
  repMin: number;
  repMax: number;
  targetRir: number;
  restSeconds: number;
  allowFailureLastSet: boolean;
  status: WorkoutExerciseStatus;
  sets: SessionSet[];
  /** 替换前原动作快照（用于当前会话内撤销替换） */
  replacedOriginal?: SessionExercise;
}

export interface RestTimer {
  exerciseId: string;
  setIndex: number;
  durationSeconds: number;
  startAt: number; // ms
  targetEndAt: number; // ms
}

export interface LastCompletedInfo {
  exerciseId: string;
  setIndex: number;
  timestamp: number;
}

export type SessionStatus = "in_progress" | "finished";

const nowIso = () => new Date().toISOString();

/** 动作进阶增量类型 → 组记录重量口径（decision 14.15 保存原始口径）。 */
const INCREMENT_TO_SCOPE: Record<IncrementType, WeightUnitScope> = {
  machine_stack: "machine_display",
  per_dumbbell: "per_dumbbell",
  barbell_total: "total",
  bodyweight: "bodyweight",
};

const emptySessionSet = (
  setIndex: number,
  weightKg: number | null,
  reps: number | null = null,
  rir: RirValue | null = null,
): SessionSet => ({
  id: uuid(),
  setIndex,
  weightKg,
  reps,
  rir,
  reachedFailure: rir === 0,
  muscleDiscomfort: false,
  jointPain: false,
  injuryPain: false,
  formBreakdown: false,
  restStartedAt: null,
  actualRestSeconds: null,
  completed: false,
  completedAt: null,
  version: 1,
  updatedAt: nowIso(),
});

/** 由持久化实体映射为内存会话动作。 */
function toSessionExercise(item: WorkoutExercise, sets: WorkoutSet[]): SessionExercise {
  const ex = EXERCISE_BY_ID.get(item.exerciseId);
  const existingSets: SessionSet[] = sets
    .filter((s) => s.workoutExerciseId === item.id)
    .sort((a, b) => a.setIndex - b.setIndex)
    .map((s) => ({
      id: s.id,
      setIndex: s.setIndex,
      weightKg: s.actualWeightKg ?? s.suggestedWeightKg ?? item.suggestedWeightKg,
      reps: s.reps ?? item.repMin,
      rir: s.rir,
      reachedFailure: s.reachedFailure,
      muscleDiscomfort: s.muscleDiscomfort,
      jointPain: s.jointPain,
      injuryPain: s.injuryPain,
      formBreakdown: s.formBreakdown ?? false,
      restStartedAt: s.restStartedAt ?? null,
      actualRestSeconds: s.actualRestSeconds ?? null,
      completed: s.status === "completed",
      completedAt: s.completedAt ?? null,
      version: s.version,
      updatedAt: s.updatedAt,
    }));

  while (existingSets.length < item.targetSets) {
    const nextIdx = existingSets.length + 1;
    existingSets.push(emptySessionSet(nextIdx, item.suggestedWeightKg, item.repMin, item.targetRir as RirValue));
  }

  return {
    id: item.id,
    exerciseId: item.exerciseId,
    suggestedWeightKg: item.suggestedWeightKg,
    weightUnitScope: item.weightUnitScope,
    targetSets: item.targetSets,
    repMin: item.repMin,
    repMax: item.repMax,
    targetRir: item.targetRir,
    restSeconds: item.restSeconds,
    allowFailureLastSet: ex?.failureAllowedLastSet ?? false,
    status: item.status,
    sets: existingSets,
  };
}

/** 由内存组记录 + 动作映射为持久化 Set。 */
function toDomainSet(item: SessionExercise, s: SessionSet): WorkoutSet {
  return {
    id: s.id,
    workoutExerciseId: item.id,
    setIndex: s.setIndex,
    suggestedWeightKg: item.suggestedWeightKg,
    actualWeightKg: s.weightKg,
    weightUnitScope: item.weightUnitScope,
    reps: s.reps,
    rir: s.rir,
    reachedFailure: s.reachedFailure,
    muscleDiscomfort: s.muscleDiscomfort,
    jointPain: s.jointPain,
    injuryPain: s.injuryPain,
    formBreakdown: s.formBreakdown ?? false,
    restStartedAt: s.restStartedAt ?? null,
    restTargetEndAt: null,
    actualRestSeconds: s.actualRestSeconds ?? null,
    completedAt: s.completed ? s.completedAt : null,
    status: s.completed ? "completed" : "planned",
    unplannedFailure: s.reachedFailure && !item.allowFailureLastSet,
    version: s.version,
    createdAt: s.completedAt ?? nowIso(),
    updatedAt: s.updatedAt,
  };
}

function toDomainItem(item: SessionExercise, sessionId: string, reason?: string): WorkoutExercise {
  return {
    id: item.id,
    sessionId,
    exerciseId: item.exerciseId,
    exerciseVersion: 1,
    orderIndex: 0,
    suggestedWeightKg: item.suggestedWeightKg,
    actualBaseWeightKg: null,
    weightUnitScope: item.weightUnitScope,
    targetSets: item.targetSets,
    repMin: item.repMin,
    repMax: item.repMax,
    targetRir: item.targetRir as RirValue,
    restSeconds: item.restSeconds,
    status: item.status,
    replacementForId: null,
    replacementReason: reason ?? null,
    skipReason: reason ?? null,
    progressionDecisionId: null,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
}

interface SessionState {
  userId: string;
  sessionId: string | null;
  sequence: string;
  planType: WorkoutPlanType;
  /** 开始训练时的准备度快照（总结页进阶建议用） */
  readinessSnapshot: ReadinessResult | null;
  startedAt: number | null;
  finishedAt: number | null;
  status: SessionStatus;
  exercises: SessionExercise[];
  activeExerciseId: string | null;
  rest: RestTimer | null;
  lastCompleted: LastCompletedInfo | null;

  start: (
    sequence: string,
    plan: GeneratedExercise[],
    readiness: ReadinessResult,
    userId?: string,
  ) => Promise<void>;
  hydrateFromDb: () => Promise<boolean>;
  addSet: (exerciseId: string) => Promise<void>;
  updateSet: (exerciseId: string, setIndex: number, patch: Partial<SessionSet>) => void;
  completeSet: (exerciseId: string, setIndex: number) => Promise<void>;
  undoLastSet: () => Promise<void>;
  clearLastCompleted: () => void;
  startRest: (exerciseId: string, setIndex: number, durationSeconds: number) => void;
  extendRest: (seconds: number) => void;
  cancelRest: () => void;
  setActiveExercise: (exerciseId: string | null) => void;
  markSkipped: (exerciseId: string, reason?: string) => Promise<void>;
  replaceExercise: (exerciseId: string, replacementId: string) => Promise<void>;
  undoReplaceExercise: (exerciseId: string) => Promise<void>;
  endSession: () => Promise<void>;
  endSessionEarly: (reason: string) => Promise<void>;
  reset: () => void;
}

function lastWeight(ex: SessionExercise): number | null {
  const last = ex.sets[ex.sets.length - 1];
  return last?.weightKg ?? ex.suggestedWeightKg;
}

/** 由生成计划映射为会话动作条目。 */
function toSessionExerciseFromPlan(g: GeneratedExercise, item: WorkoutExercise): SessionExercise {
  const sets: SessionSet[] = [];
  for (let i = 1; i <= g.targetSets; i++) {
    sets.push(emptySessionSet(i, g.suggestedWeightKg, g.repMin, g.targetRir as RirValue));
  }
  return {
    id: item.id,
    exerciseId: g.exercise.id,
    suggestedWeightKg: g.suggestedWeightKg,
    weightUnitScope: g.weightUnitScope,
    targetSets: g.targetSets,
    repMin: g.repMin,
    repMax: g.repMax,
    targetRir: g.targetRir,
    restSeconds: g.restSeconds,
    allowFailureLastSet: g.allowFailureLastSet,
    status: "in_progress",
    sets,
  };
}

export const useSessionStore = create<SessionState>((set, get) => ({
  userId: "user-default",
  sessionId: null,
  sequence: "A",
  planType: "normal",
  readinessSnapshot: null,
  startedAt: null,
  finishedAt: null,
  status: "in_progress",
  exercises: [],
  activeExerciseId: null,
  rest: null,
  lastCompleted: null,

  start: async (sequence, plan, readiness, userId = "user-default") => {
    // 开始新训练前清除旧休息计时
    void db.meta.delete("active_rest_timer");
    const ws = await createWorkout(sequence, userId, plan, readiness);
    const byItem = new Map(ws.items.map((i) => [i.exerciseId, i]));
    const exercises = plan.map((g) => toSessionExerciseFromPlan(g, byItem.get(g.exercise.id)!));
    set({
      userId,
      sessionId: ws.session.id,
      sequence,
      planType: readiness.planType,
      readinessSnapshot: readiness,
      startedAt: Date.now(),
      finishedAt: null,
      status: "in_progress",
      exercises,
      activeExerciseId: exercises[0]?.exerciseId ?? null,
      rest: null,
      lastCompleted: null,
    });
  },

  hydrateFromDb: async () => {
    const ws = await loadActiveWorkout();
    if (!ws) return false;
    const exercises = ws.items.map((item) => toSessionExercise(item, ws.sets));
    const planType: WorkoutPlanType = ws.session.planType ?? "normal";

    // 读取持久化的 RestTimer 状态
    let restoredRest: RestTimer | null = null;
    try {
      const restMeta = await db.meta.get("active_rest_timer");
      if (restMeta && restMeta.value) {
        const timerData = restMeta.value as RestTimer;
        // 若已超时，清除 DB
        if (Date.now() >= timerData.targetEndAt) {
          void db.meta.delete("active_rest_timer");
        } else {
          restoredRest = timerData;
        }
      }
    } catch {
      // 忽略读取异常
    }

    set({
      userId: ws.session.userId,
      sessionId: ws.session.id,
      sequence: ws.session.notes?.match(/sequence=([A-E])/)?.[1] ?? "A",
      planType,
      readinessSnapshot: ws.session.readinessSnapshot as ReadinessResult | null,
      startedAt: new Date(ws.session.startedAt).getTime(),
      finishedAt: ws.session.finishedAt ? new Date(ws.session.finishedAt).getTime() : null,
      status: "in_progress",
      exercises,
      activeExerciseId: exercises[0]?.exerciseId ?? null,
      rest: restoredRest,
      lastCompleted: null,
    });
    return exercises.length > 0;
  },

  addSet: async (exerciseId) => {
    const cur = get().exercises.find((e) => e.exerciseId === exerciseId);
    if (!cur) return;
    const prevSet = cur.sets[cur.sets.length - 1];
    const next = emptySessionSet(
      cur.sets.length + 1,
      prevSet?.weightKg ?? lastWeight(cur),
      prevSet?.reps ?? cur.repMin,
      prevSet?.rir ?? (cur.targetRir as RirValue),
    );
    set({
      exercises: get().exercises.map((ex) =>
        ex.exerciseId === exerciseId ? { ...ex, sets: [...ex.sets, next] } : ex,
      ),
    });
    await saveSet(toDomainSet(cur, next), get().userId);
  },

  updateSet: (exerciseId, setIndex, patch) => {
    let updated: SessionSet | null = null;
    const exercises = get().exercises.map((ex) =>
      ex.exerciseId === exerciseId
        ? {
            ...ex,
            sets: ex.sets.map((s) => {
              if (s.setIndex !== setIndex) return s;
              updated = { ...s, ...patch, version: s.version + 1, updatedAt: nowIso() };
              return updated;
            }),
          }
        : ex,
    );
    set({ exercises });
    const item = exercises.find((e) => e.exerciseId === exerciseId);
    if (item && updated) void saveSet(toDomainSet(item, updated), get().userId);
  },

  completeSet: async (exerciseId, setIndex) => {
    const item = get().exercises.find((e) => e.exerciseId === exerciseId);
    if (!item) return;
    const target = item.sets.find((s) => s.setIndex === setIndex);
    if (!target) return;

    // 若此前已有运行中的休息计时，结算前一组实际休息秒数
    const ongoingRest = get().rest;
    if (ongoingRest) {
      const restSec = Math.max(0, Math.round((Date.now() - ongoingRest.startAt) / 1000));
      const prevEx = get().exercises.find((e) => e.exerciseId === ongoingRest.exerciseId);
      const prevSet = prevEx?.sets.find((s) => s.setIndex === ongoingRest.setIndex);
      if (prevEx && prevSet) {
        const patchedPrev = { ...prevSet, actualRestSeconds: restSec, updatedAt: nowIso() };
        void saveSet(toDomainSet(prevEx, patchedPrev), get().userId);
      }
    }

    const done: SessionSet = {
      ...target,
      completed: true,
      completedAt: nowIso(),
      restStartedAt: nowIso(),
      version: target.version + 1,
      updatedAt: nowIso(),
    };

    // 默认值预填：将本次完成的实际重量/次数同步给后续未完成组作为智能默认值
    const updatedSets = item.sets.map((s) => {
      if (s.setIndex === setIndex) return done;
      if (s.setIndex > setIndex && !s.completed) {
        return {
          ...s,
          weightKg: s.weightKg ?? done.weightKg,
          reps: s.reps ?? done.reps,
          rir: s.rir ?? done.rir,
        };
      }
      return s;
    });

    set({
      exercises: get().exercises.map((ex) =>
        ex.exerciseId === exerciseId ? { ...ex, sets: updatedSets } : ex,
      ),
      lastCompleted: { exerciseId, setIndex, timestamp: Date.now() },
    });

    await saveSet(toDomainSet(item, done), get().userId);
    get().startRest(exerciseId, setIndex, item.restSeconds);
  },

  undoLastSet: async () => {
    const last = get().lastCompleted;
    if (!last) return;
    const item = get().exercises.find((e) => e.exerciseId === last.exerciseId);
    if (!item) return;
    const target = item.sets.find((s) => s.setIndex === last.setIndex);
    if (!target) return;

    const reverted: SessionSet = {
      ...target,
      completed: false,
      completedAt: null,
      restStartedAt: null,
      actualRestSeconds: null,
      version: target.version + 1,
      updatedAt: nowIso(),
    };

    set({
      exercises: get().exercises.map((ex) =>
        ex.exerciseId === last.exerciseId
          ? { ...ex, sets: ex.sets.map((s) => (s.setIndex === last.setIndex ? reverted : s)) }
          : ex,
      ),
      lastCompleted: null,
      rest: null,
    });

    await saveSet(toDomainSet(item, reverted), get().userId);
    void db.meta.delete("active_rest_timer");
  },

  clearLastCompleted: () => set({ lastCompleted: null }),

  startRest: (exerciseId, setIndex, durationSeconds) => {
    const restData: RestTimer = {
      exerciseId,
      setIndex,
      durationSeconds,
      startAt: Date.now(),
      targetEndAt: Date.now() + durationSeconds * 1000,
    };
    set({ rest: restData });
    void db.meta.put({
      key: "active_rest_timer",
      value: restData,
      updatedAt: nowIso(),
    });
  },

  extendRest: (seconds) => {
    const rest = get().rest;
    if (!rest) return;
    const updated: RestTimer = {
      ...rest,
      targetEndAt: rest.targetEndAt + seconds * 1000,
      durationSeconds: rest.durationSeconds + seconds,
    };
    set({ rest: updated });
    void db.meta.put({
      key: "active_rest_timer",
      value: updated,
      updatedAt: nowIso(),
    });
  },

  cancelRest: () => {
    const rest = get().rest;
    if (rest) {
      const restSec = Math.max(0, Math.round((Date.now() - rest.startAt) / 1000));
      const curEx = get().exercises.find((e) => e.exerciseId === rest.exerciseId);
      const curSet = curEx?.sets.find((s) => s.setIndex === rest.setIndex);
      if (curEx && curSet) {
        const patched = { ...curSet, actualRestSeconds: restSec, updatedAt: nowIso() };
        void saveSet(toDomainSet(curEx, patched), get().userId);
      }
    }
    set({ rest: null });
    void db.meta.delete("active_rest_timer");
  },

  setActiveExercise: (exerciseId) => set({ activeExerciseId: exerciseId }),

  markSkipped: async (exerciseId, reason = "user_skipped") => {
    const cur = get().exercises.find((e) => e.exerciseId === exerciseId);
    if (!cur) return;
    const updated: SessionExercise = { ...cur, status: "skipped" };
    set({
      exercises: get().exercises.map((ex) => (ex.exerciseId === exerciseId ? updated : ex)),
    });
    const sid = get().sessionId ?? cur.id;
    await saveExerciseItem(toDomainItem(updated, sid, reason), get().userId);
  },

  replaceExercise: async (exerciseId, replacementId) => {
    const cur = get().exercises.find((e) => e.exerciseId === exerciseId);
    if (!cur) return;
    const ex = EXERCISE_BY_ID.get(replacementId);
    const newItem: SessionExercise = {
      id: uuid(),
      exerciseId: replacementId,
      suggestedWeightKg: null,
      weightUnitScope: INCREMENT_TO_SCOPE[ex?.incrementType ?? "machine_stack"],
      targetSets: ex?.defaultSets ?? 3,
      repMin: ex?.repMin ?? 8,
      repMax: ex?.repMax ?? 12,
      targetRir: ex?.targetRir ?? 2,
      restSeconds: ex?.restSeconds ?? 120,
      allowFailureLastSet: ex?.failureAllowedLastSet ?? false,
      status: "in_progress",
      sets: [emptySessionSet(1, null)],
      replacedOriginal: cur, // 保留原动作快照（含已完成组）
    };
    set({
      exercises: get().exercises.map((ex) => (ex.exerciseId === exerciseId ? newItem : ex)),
      activeExerciseId: replacementId,
    });
    const sid = get().sessionId ?? newItem.id;
    await saveExerciseItem(toDomainItem(newItem, sid, "replaced_with_own_history"), get().userId);
    await saveSet(toDomainSet(newItem, newItem.sets[0]!), get().userId);
    void saveExerciseItem(toDomainItem({ ...cur, status: "replaced" }, sid, "original_replaced"), get().userId);
  },

  undoReplaceExercise: async (exerciseId) => {
    const cur = get().exercises.find((e) => e.exerciseId === exerciseId);
    if (!cur || !cur.replacedOriginal) return;
    const restored: SessionExercise = {
      ...cur.replacedOriginal,
      status: "in_progress",
    };
    set({
      exercises: get().exercises.map((ex) => (ex.exerciseId === exerciseId ? restored : ex)),
      activeExerciseId: restored.exerciseId,
    });
    const sid = get().sessionId ?? restored.id;
    await saveExerciseItem(toDomainItem(restored, sid, "restored_original"), get().userId);
    for (const s of restored.sets) {
      await saveSet(toDomainSet(restored, s), get().userId);
    }
  },

  endSession: async () => {
    const sid = get().sessionId;
    if (sid) await finishSession(sid, "completed", get().userId);
    void db.meta.delete("active_rest_timer");
    set({ status: "finished", finishedAt: Date.now(), rest: null });
  },

  endSessionEarly: async (reason: string) => {
    const sid = get().sessionId;
    if (sid) await finishSession(sid, "ended_early", get().userId, reason);
    void db.meta.delete("active_rest_timer");
    set({ status: "finished", finishedAt: Date.now(), rest: null });
  },

  reset: () => {
    void db.meta.delete("active_rest_timer");
    set({
      sessionId: null,
      sequence: "A",
      planType: "normal",
      readinessSnapshot: null,
      startedAt: null,
      finishedAt: null,
      status: "in_progress",
      exercises: [],
      activeExerciseId: null,
      rest: null,
      lastCompleted: null,
    });
  },
}));

/** 派生：已完成组数（跨动作）。 */
export function completedSetCount(exercises: SessionExercise[]): number {
  return exercises.reduce((acc, ex) => acc + ex.sets.filter((s) => s.completed).length, 0);
}
